/**
 * SnowballWizard — P14 3-step onboarding funnel.
 * Step 1: About You — salary surplus, city
 * Step 2: First Property — price, type, RTM/UC
 * Step 3: Add more or simulate
 * Auto-fills expert settings based on city + type.
 */
import { useState } from "react";
import { ArrowRight, ArrowLeft, User, Building, Sparkles, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

// City defaults — appreciation + rent escalation typical for major Indian cities.
const CITY_DEFAULTS = {
  "mumbai": { appreciation: 6, rent_growth: 6, label: "Mumbai" },
  "bangalore": { appreciation: 8, rent_growth: 7, label: "Bengaluru" },
  "pune": { appreciation: 7, rent_growth: 6, label: "Pune" },
  "hyderabad": { appreciation: 8, rent_growth: 7, label: "Hyderabad" },
  "chennai": { appreciation: 6, rent_growth: 5, label: "Chennai" },
  "delhi": { appreciation: 7, rent_growth: 6, label: "Delhi NCR" },
  "kolkata": { appreciation: 5, rent_growth: 5, label: "Kolkata" },
  "other": { appreciation: 7, rent_growth: 5, label: "Other / Tier-2" },
};

const TYPE_DEFAULTS = {
  "flat": { appreciation: 7, monthly_rent_pct: 0.005, loan_rate: 8.75, label: "Flat / Apartment", tds: 0 },
  "plot": { appreciation: 12, monthly_rent_pct: 0, loan_rate: 9.50, label: "Plot (land)", tds: 0 },
  "commercial": { appreciation: 7, monthly_rent_pct: 0.0075, loan_rate: 10.0, label: "Commercial", tds: 10 },
};

export default function SnowballWizard({ onComplete, onCancel }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    monthly_salary_surplus: 80000,
    starting_snowball_fund: 1500000,
    city: "pune",
    property: {
      type: "flat",
      price: 5000000,
      is_under_construction: false,
      possession_month: 24,
    },
  });

  const cityCfg = CITY_DEFAULTS[data.city];
  const typeCfg = TYPE_DEFAULTS[data.property.type];

  const compileRequest = () => {
    const monthly_rent = Math.round(data.property.price * typeCfg.monthly_rent_pct);
    return {
      horizon_months: 240,
      starting_snowball_fund: data.starting_snowball_fund,
      monthly_salary_surplus: data.monthly_salary_surplus,
      annual_salary_growth_pct: 6,
      snowball_fund_yield_pct: 4,
      properties: [{
        id: `wizard-${Date.now()}`,
        name: `${cityCfg.label} ${typeCfg.label}`,
        type: data.property.type,
        price: data.property.price,
        down_payment_pct: 20,
        loan_rate_pct: typeCfg.loan_rate,
        loan_tenure_years: 20,
        monthly_rent,
        annual_rent_increase_pct: cityCfg.rent_growth,
        annual_appreciation_pct: typeCfg.type === "plot" ? typeCfg.appreciation : cityCfg.appreciation,
        is_under_construction: data.property.is_under_construction,
        possession_month: data.property.is_under_construction ? data.property.possession_month : 0,
        desired_buy_month: 0,
        rental_start_month: null,
        payment_plan: data.property.is_under_construction ? "clp" : "lump_sum",
        clp_tranches: [],
        monthly_maintenance: Math.round(monthly_rent * 0.05),
        property_tax_annual: Math.round(data.property.price * 0.001),
        vacancy_pct: 5,
        rent_tds_pct: typeCfg.tds,
      }],
    };
  };

  return (
    <div className="card-flat p-6 space-y-5" data-testid="snowball-wizard">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-2xl">Quick-start wizard</h3>
        <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="wizard-cancel">
          Skip — I&apos;ll fill manually
        </button>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between border-b border-[#e5e1d6] pb-3">
        <StepBadge n={1} label="About You" active={step === 1} done={step > 1} />
        <div className="flex-1 h-px bg-[#e5e1d6] mx-3" />
        <StepBadge n={2} label="First Property" active={step === 2} done={step > 2} />
        <div className="flex-1 h-px bg-[#e5e1d6] mx-3" />
        <StepBadge n={3} label="Review" active={step === 3} done={false} />
      </div>

      {/* Step 1 — About You */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <User size={16} className="text-[#0B7B6E]" />
            <span className="text-sm font-medium">Tell us about you</span>
          </div>
          <p className="text-sm text-muted-foreground">
            How much can you stash into your &quot;snowball fund&quot; every month? This is the surplus
            after rent/EMI/expenses — money that compounds until it buys your next property.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Monthly salary surplus (₹)</Label>
              <Input
                type="number"
                value={data.monthly_salary_surplus}
                onChange={(e) => setData({ ...data, monthly_salary_surplus: Number(e.target.value) || 0 })}
                className="font-mono mt-1"
                data-testid="wizard-salary"
              />
            </div>
            <div>
              <Label className="text-xs">Starting snowball fund (₹)</Label>
              <Input
                type="number"
                value={data.starting_snowball_fund}
                onChange={(e) => setData({ ...data, starting_snowball_fund: Number(e.target.value) || 0 })}
                className="font-mono mt-1"
                data-testid="wizard-fund"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">City</Label>
            <select
              value={data.city}
              onChange={(e) => setData({ ...data, city: e.target.value })}
              className="w-full mt-1 border border-input rounded-md bg-white px-3 py-2 text-sm"
              data-testid="wizard-city"
            >
              {Object.entries(CITY_DEFAULTS).map(([id, cfg]) => (
                <option key={id} value={id}>{cfg.label}</option>
              ))}
            </select>
            <div className="text-[11px] text-muted-foreground mt-1">
              We auto-fill appreciation ({cityCfg.appreciation}%/yr) and rent growth ({cityCfg.rent_growth}%/yr) for this city.
            </div>
          </div>
        </div>
      )}

      {/* Step 2 — First property */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Building size={16} className="text-[#0B7B6E]" />
            <span className="text-sm font-medium">Your first property</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <select
                value={data.property.type}
                onChange={(e) => setData({ ...data, property: { ...data.property, type: e.target.value } })}
                className="w-full mt-1 border border-input rounded-md bg-white px-3 py-2 text-sm"
                data-testid="wizard-type"
              >
                {Object.entries(TYPE_DEFAULTS).map(([id, cfg]) => (
                  <option key={id} value={id}>{cfg.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Price (₹)</Label>
              <Input
                type="number"
                value={data.property.price}
                onChange={(e) => setData({ ...data, property: { ...data.property, price: Number(e.target.value) || 0 } })}
                className="font-mono mt-1"
                data-testid="wizard-price"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm py-2">
                <input
                  type="checkbox"
                  checked={data.property.is_under_construction}
                  onChange={(e) => setData({ ...data, property: { ...data.property, is_under_construction: e.target.checked } })}
                  data-testid="wizard-uc"
                />
                Under construction
              </label>
            </div>
            {data.property.is_under_construction && (
              <div>
                <Label className="text-xs">Possession in (months)</Label>
                <Input
                  type="number"
                  value={data.property.possession_month}
                  onChange={(e) => setData({ ...data, property: { ...data.property, possession_month: Number(e.target.value) || 0 } })}
                  className="font-mono mt-1"
                  min={1} max={60}
                />
              </div>
            )}
          </div>
          <div className="rounded-md bg-[#fafaf7] border border-[#e5e1d6] p-3 text-xs">
            <strong>We auto-fill:</strong> loan rate {typeCfg.loan_rate}%, monthly rent ₹
            {Math.round(data.property.price * typeCfg.monthly_rent_pct).toLocaleString("en-IN")},
            appreciation {data.property.type === "plot" ? typeCfg.appreciation : cityCfg.appreciation}%/yr.
            {typeCfg.tds > 0 && <> Commercial properties have {typeCfg.tds}% TDS on rent.</>}
            {data.property.type === "plot" && <> Plots have zero rent.</>}
          </div>
        </div>
      )}

      {/* Step 3 — Review */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} className="text-[#C85A32]" />
            <span className="text-sm font-medium">Ready to simulate</span>
          </div>
          <p className="text-sm text-muted-foreground">
            We&apos;ve set sensible defaults for everything else (down payment 20%, tenure 20 years, 5% vacancy, 6% salary growth).
            You can fine-tune any of this after the simulation runs.
          </p>
          <div className="rounded-md bg-[#fafaf7] border border-[#e5e1d6] p-4 space-y-1 text-sm">
            <div><strong>You:</strong> ₹{data.monthly_salary_surplus.toLocaleString("en-IN")}/mo surplus, ₹{data.starting_snowball_fund.toLocaleString("en-IN")} starting fund</div>
            <div><strong>City:</strong> {cityCfg.label} — {cityCfg.appreciation}% appreciation, {cityCfg.rent_growth}% rent growth</div>
            <div><strong>Property:</strong> {typeCfg.label} at ₹{(data.property.price/100000).toFixed(1)}L {data.property.is_under_construction ? `(UC, ${data.property.possession_month}mo to possession)` : "(ready to move)"}</div>
          </div>
        </div>
      )}

      {/* Step nav */}
      <div className="flex items-center justify-between pt-3 border-t border-[#e5e1d6]">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          data-testid="wizard-back"
        >
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        {step < 3 ? (
          <Button onClick={() => setStep(step + 1)} data-testid="wizard-next">
            Next <ArrowRight size={14} className="ml-1" />
          </Button>
        ) : (
          <Button onClick={() => onComplete(compileRequest())} data-testid="wizard-complete">
            Run simulation <Sparkles size={14} className="ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

function StepBadge({ n, label, active, done }) {
  return (
    <div className={`flex items-center gap-2 ${active ? "" : "opacity-60"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
        done ? "bg-emerald-600 text-white" :
        active ? "bg-[#C85A32] text-white" :
        "bg-[#e5e1d6] text-foreground"
      }`}>
        {done ? <Check size={14} /> : n}
      </div>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}
